import React, { useState, useEffect } from 'react';
import { Shield, Building2, User, ArrowRight } from 'lucide-react';
import logo from './images/logo.svg';
import { login, getUserInfo } from '../../services/userService';

const PERSONAS = [
  { icon: Shield,    label: 'Law enforcement agencies' },
  { icon: Building2, label: 'Businesses & customers' },
  { icon: User,      label: 'Off-duty officers' },
];

const Home = () => {
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    getUserInfo()
      .then(() => {
        // Valid session exists — skip the landing page entirely
        window.location.href = '/home';
      })
      .catch(() => {
        // No valid session — show the landing page as normal
        setCheckingAuth(false);
      });
  }, []);

  if (checkingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#F7F8FA]">
        <div className="w-10 h-10 border-4 border-t-[#404880] border-[#404880]/20 rounded-full animate-spin" />
      </div>
    );
  }

  const handleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await login();
      window.location.href = resp.data.authUrl;
    } catch (err) {
      setError('Unable to initiate sign in. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F7F8FA] font-inter antialiased px-6 py-16">

      {/* Brand wordmark */}
      <div className="flex flex-col items-center mb-8">
        <div className="flex flex-col items-start mb-5">
          <div className="flex items-center gap-3 mb-2">
            <img src={logo} alt="" className="w-9 h-9 object-contain flex-shrink-0 relative translate-y-[2px]" />
            <span className="text-4xl sm:text-5xl font-bold text-[#404880] tracking-tight leading-[1]">Officer.app</span>
          </div>
          {/* Pink accent line — starts where the text starts (w-9 + gap-3 = ml-12) */}
          <div className="h-[3px] w-28 rounded-full bg-[#CC457D] ml-12" />
        </div>
        <p className="text-sm text-gray-400 text-center max-w-xs leading-relaxed">
          Connecting law enforcement agencies, businesses, and officers — all in one place.
        </p>
      </div>

      {/* Persona pills */}
      <div className="flex flex-wrap justify-center gap-2.5 mb-10">
        {PERSONAS.map(({ icon: Icon, label }) => (
          <span
            key={label}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#404880]/20 bg-white px-3.5 py-1.5 text-xs font-medium text-[#404880]"
          >
            <Icon size={12} />
            {label}
          </span>
        ))}
      </div>

      {/* CTA card */}
      <div className="w-full max-w-xs">
        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm text-center">
            {error}
          </div>
        )}

        <button
          onClick={handleSignIn}
          disabled={loading}
          className="w-full py-3 px-4 rounded-xl bg-[#404880] hover:bg-[#353c6e] text-white text-sm font-semibold transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? 'Redirecting…' : 'Sign in to your portal'}
        </button>

        <a
          href="mailto:hello@officer.app?subject=Enquiry%20%2F%20Question%20%7C%20platform.officer.app"
          className="mt-3 w-full flex items-center justify-center gap-1 py-2.5 px-4 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors no-underline"
        >
          Contact us
          <ArrowRight size={13} />
        </a>
      </div>

      {/* Footer */}
      <p className="mt-12 text-xs text-gray-400 text-center">
        Learn more at{' '}
        <a
          href="https://officer.app"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#404880] hover:underline"
        >
          officer.app
        </a>
        {' '}· &copy; {new Date().getFullYear()} Officer.app
      </p>
    </div>
  );
};

export default Home;